// ── PDF Export Utility ─────────────────────────────────────────────────────
// Uses jsPDF + jspdf-autotable (install: npm install jspdf jspdf-autotable)
// Generates a professional multi-page loan portfolio report

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ── Colours matching app theme ─────────────────────────────────────────────
const GOLD       = [201, 168, 76];
const GOLD_DARK  = [140, 110, 40];
const DARK       = [15, 15, 20];
const DARK2      = [22, 22, 31];
const WHITE      = [255, 255, 255];
const LIGHT_GRAY = [245, 243, 238];
const MID_GRAY   = [180, 175, 165];
const TEXT_DARK  = [30, 28, 20];
const GREEN      = [44, 122, 82];
const RED        = [192, 57, 43];

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt  = (n) => `Rs.${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

function calculateInterest(principal, annualRate, startDate, endDate = new Date()) {
  const start = new Date(startDate);
  const end   = new Date(endDate);
  let years  = end.getFullYear() - start.getFullYear();
  let months = end.getMonth()   - start.getMonth();
  let totalMonths = years * 12 + months;
  const startDay = start.getDate();
  const endDay   = end.getDate();
  if (endDay >= startDay) {
    const daysInEnd = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    totalMonths += (endDay - startDay) / daysInEnd;
  } else {
    totalMonths -= 1;
    const daysInPrev = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    totalMonths += (daysInPrev - startDay + endDay) / daysInPrev;
  }
  return Number(((principal * annualRate * totalMonths) / 100).toFixed(2));
}

function calcDuration(startDate) {
  const start = new Date(startDate);
  const now   = new Date();
  let y = now.getFullYear() - start.getFullYear();
  let m = now.getMonth()   - start.getMonth();
  let d = now.getDate()    - start.getDate();
  if (d < 0) { m--; d += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
  if (m < 0) { y--; m += 12; }
  return `${y}Y ${m}M ${d}D`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Draw page header ───────────────────────────────────────────────────────
function drawHeader(doc, pageWidth, title = "Loan Portfolio Report") {
  // Dark header band
  doc.setFillColor(...DARK);
  doc.rect(0, 0, pageWidth, 28, "F");

  // Gold accent bar
  doc.setFillColor(...GOLD);
  doc.rect(0, 28, pageWidth, 2, "F");

  // App name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...GOLD);
  doc.text("✦ MyLoanApp", 14, 18);

  // Report title (right side)
  doc.setFontSize(9);
  doc.setTextColor(...MID_GRAY);
  doc.text(title, pageWidth - 14, 13, { align: "right" });

  // Generated date
  const now = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
  doc.text(`Generated: ${now}`, pageWidth - 14, 22, { align: "right" });
}

// ── Draw page footer ───────────────────────────────────────────────────────
function drawFooter(doc, pageWidth, pageHeight, pageNum, totalPages) {
  doc.setFillColor(...DARK);
  doc.rect(0, pageHeight - 14, pageWidth, 14, "F");

  doc.setFillColor(...GOLD);
  doc.rect(0, pageHeight - 14, pageWidth, 1, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MID_GRAY);
  doc.text("MyLoanApp — Confidential", 14, pageHeight - 5);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, pageHeight - 5, { align: "right" });
}

// ── Draw summary cards row ─────────────────────────────────────────────────
function drawSummaryCards(doc, y, pageWidth, loans) {
  const totalPrincipal = loans.reduce((s, l) => s + Number(l.amount || 0), 0);
  const totalInterest  = loans.reduce((s, l) => s + calculateInterest(l.amount, l.interestRate, l.duration), 0);
  const totalOutstanding = loans.reduce((l2, l) => {
    const int  = calculateInterest(l.amount, l.interestRate, l.duration);
    const due  = Number(l.amount) + int - (l.totalCredit || 0) + (l.totalDebit || 0);
    return l2 + due;
  }, 0);
  const totalCredited = loans.reduce((s, l) => s + (l.totalCredit || 0), 0);
  const activeCount   = loans.filter(l => l.status === "Active").length;

  const cards = [
    { label: "Total Invested",     value: fmt(totalPrincipal),    color: GOLD },
    { label: "Total Interest",     value: fmt(totalInterest),     color: GOLD },
    { label: "Total Outstanding",  value: fmt(totalOutstanding),  color: RED  },
    { label: "Total Recovered",    value: fmt(totalCredited),     color: GREEN },
    { label: "Active Loans",       value: `${activeCount} / ${loans.length}`, color: GOLD },
  ];

  const cardW = (pageWidth - 28 - (cards.length - 1) * 4) / cards.length;
  let x = 14;

  cards.forEach(card => {
    // Card background
    doc.setFillColor(...LIGHT_GRAY);
    doc.roundedRect(x, y, cardW, 22, 3, 3, "F");

    // Gold top bar
    doc.setFillColor(...card.color);
    doc.roundedRect(x, y, cardW, 3, 1.5, 1.5, "F");

    // Label
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MID_GRAY);
    doc.text(card.label.toUpperCase(), x + cardW / 2, y + 9, { align: "center" });

    // Value
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_DARK);
    doc.text(card.value, x + cardW / 2, y + 17, { align: "center" });

    x += cardW + 4;
  });

  return y + 28;
}

// ── Main export function ───────────────────────────────────────────────────
export async function exportLoansPDF(loans, userName = "Admin") {
  const doc       = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ── Page 1: Portfolio Overview ─────────────────────────────────────────
  drawHeader(doc, pageWidth, "Portfolio Overview");

  // Section title
  let y = 38;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...TEXT_DARK);
  doc.text("Loan Portfolio Summary", 14, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MID_GRAY);
  doc.text(`Account: ${userName}   |   Total Records: ${loans.length}`, 14, y + 6);

  y += 14;

  // Summary cards
  y = drawSummaryCards(doc, y, pageWidth, loans);

  // Section divider
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.line(14, y, pageWidth - 14, y);
  y += 6;

  // Loans table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_DARK);
  doc.text("All Loans", 14, y);
  y += 4;

  const tableRows = loans.map((loan, i) => {
    const interest = calculateInterest(loan.amount, loan.interestRate, loan.duration);
    const totalDue = Number(loan.amount) + interest - (loan.totalCredit || 0) + (loan.totalDebit || 0);
    return [
      i + 1,
      loan.borrowerName,
      loan.phone || "—",
      fmt(loan.amount),
      `${loan.interestRate}%`,
      formatDate(loan.duration),
      calcDuration(loan.duration),
      fmt(interest),
      fmt(totalDue),
      fmt(loan.totalCredit || 0),
      fmt(loan.totalDebit  || 0),
      loan.status,
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [[
      "#", "Borrower Name", "Phone", "Principal", "Rate",
      "Start Date", "Duration", "Interest", "Total Due",
      "Credited", "Debited", "Status"
    ]],
    body: tableRows,
    margin: { left: 14, right: 14 },
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      font: "helvetica",
      textColor: TEXT_DARK,
      lineColor: [220, 215, 205],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: DARK,
      textColor: GOLD,
      fontSize: 7.5,
      fontStyle: "bold",
      halign: "center",
    },
    alternateRowStyles: { fillColor: [250, 248, 244] },
    bodyStyles: { halign: "center" },
    columnStyles: {
      1: { halign: "left", fontStyle: "bold" },
      2: { halign: "left" },
      11: {
        fontStyle: "bold",
        // status coloring handled in didParseCell
      },
    },
    didParseCell(data) {
      if (data.section === "body") {
        // Status column
        if (data.column.index === 11) {
          data.cell.styles.textColor = data.cell.raw === "Active" ? GREEN : RED;
        }
        // Total Due column
        if (data.column.index === 8) {
          data.cell.styles.textColor = GOLD_DARK;
          data.cell.styles.fontStyle = "bold";
        }
        // Credited
        if (data.column.index === 9) data.cell.styles.textColor = RED;
        // Debited
        if (data.column.index === 10) data.cell.styles.textColor = GREEN;
      }
    },
    didDrawPage(data) {
      const pg = doc.internal.getCurrentPageInfo().pageNumber;
      drawHeader(doc, pageWidth, "Portfolio Overview");
      drawFooter(doc, pageWidth, pageHeight, pg, "—");
    },
  });

  // ── Page 2+: Individual Loan Detail Pages ─────────────────────────────
  loans.forEach((loan) => {
    doc.addPage();
    drawHeader(doc, pageWidth, "Loan Detail");

    const interest = calculateInterest(loan.amount, loan.interestRate, loan.duration);
    const totalDue = Number(loan.amount) + interest - (loan.totalCredit || 0) + (loan.totalDebit || 0);

    let y2 = 38;

    // Borrower name heading
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...TEXT_DARK);
    doc.text(loan.borrowerName, 14, y2);

    // Status badge
    const badgeColor = loan.status === "Active" ? GREEN : RED;
    doc.setFillColor(...badgeColor);
    doc.roundedRect(pageWidth - 44, y2 - 6, 30, 8, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...WHITE);
    doc.text(loan.status.toUpperCase(), pageWidth - 29, y2 - 0.5, { align: "center" });

    y2 += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text(`Phone: ${loan.phone || "—"}   |   Loan ID: ${loan._id}`, 14, y2);

    y2 += 8;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.line(14, y2, pageWidth - 14, y2);
    y2 += 8;

    // Two-column info grid
    const col1X = 14, col2X = pageWidth / 2 + 10;
    const infoItems = [
      ["Principal Amount",  fmt(loan.amount)],
      ["Interest Rate",     `${loan.interestRate}% per annum`],
      ["Loan Start Date",   formatDate(loan.duration)],
      ["Duration",          calcDuration(loan.duration)],
      ["Accrued Interest",  fmt(interest)],
      ["Total Due",         fmt(totalDue)],
      ["Total Credited",    fmt(loan.totalCredit || 0)],
      ["Total Debited",     fmt(loan.totalDebit  || 0)],
    ];

    infoItems.forEach(([label, value], i) => {
      const x = i % 2 === 0 ? col1X : col2X;
      const rowY = y2 + Math.floor(i / 2) * 12;

      doc.setFillColor(...LIGHT_GRAY);
      doc.roundedRect(x, rowY - 5, pageWidth / 2 - 20, 10, 2, 2, "F");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...MID_GRAY);
      doc.text(label, x + 4, rowY + 0.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      const valueColor =
        label === "Total Due"      ? GOLD_DARK :
        label === "Total Credited" ? RED :
        label === "Total Debited"  ? GREEN : TEXT_DARK;
      doc.setTextColor(...valueColor);
      doc.text(value, x + pageWidth / 2 - 24, rowY + 0.5, { align: "right" });
    });

    y2 += Math.ceil(infoItems.length / 2) * 12 + 8;

    // Draw footer now (page number will be updated at end)
    drawFooter(doc, pageWidth, pageHeight, doc.internal.getCurrentPageInfo().pageNumber, "—");
  });

  // ── Fix page numbers ───────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, pageWidth, pageHeight, i, totalPages);
  }

  // ── Save ───────────────────────────────────────────────────────────────
  const dateStr = new Date().toISOString().split("T")[0];
  doc.save(`LoanPortfolio_${dateStr}.pdf`);
}
